import { apiv1 } from '@/adapters';
import type {
  PageTenantModel,
  SlugModel,
  TenantModel,
} from '@/adapters/apiv1.g';
import { extractAccepted, extractTenant, unwrapData } from '@/adapters/shared';
import type { Tenant } from '@/types/core/tenant';
import type { Page, PageRequest } from '@/types/shared/page';
import { toApiQuery } from '@/types/shared/page';
import type { UUID } from '@/utils/uuid';

export const tenantService = {
  /**
   * List tenants visible to the current user/profile.
   * @param opts optional paging arguments
   * @returns array of Tenant objects (empty array if none)
   */
  listTenants: async (query?: PageRequest): Promise<Page<Tenant>> => {
    const apiQuery = toApiQuery(query);
    const res = await apiv1.listMyTenants(apiQuery);
    const page = unwrapData<PageTenantModel>(res);
    const models: TenantModel[] = Array.isArray(page?.models)
      ? (page.models as TenantModel[])
      : [];
    const next = page?.next ?? null;
    const prev = page?.prev ?? null;
    return {
      items: models.map(extractTenant),
      next,
      prev,
      limit: query?.limit,
    };
  },

  /**
   * Find a tenant by id.
   * @returns Tenant or null when not found
   */
  findTenant: async (tenantId: UUID): Promise<Tenant | null> => {
    const res = await apiv1.findTenant(tenantId);
    const model = unwrapData<TenantModel>(res);
    if (!model) return null;
    return extractTenant(model);
  },

  /**
   * Find a tenant by slug.
   * This resolves the slug record to the underlying tenant and returns the
   * full Tenant object or null when not found (404).
   * Throws for non-OK responses so React Query records a proper error instead
   * of caching a null as a successful "not found" result.
   */
  findSlug: async (slug: string): Promise<Tenant | null> => {
    const res = await apiv1.findSlug(slug);
    if (!res.ok) {
      if (res.status === 404) return null;
      throw new Error(
        res.error?.message ?? `Failed to load tenant (${res.status})`
      );
    }
    const slugModel = unwrapData<SlugModel>(res);
    if (!slugModel?.tenant_id) return null;

    const tenantRes = await apiv1.findTenant(slugModel.tenant_id);
    if (!tenantRes.ok) {
      if (tenantRes.status === 404) return null;
      throw new Error(
        tenantRes.error?.message ??
          `Failed to load tenant (${tenantRes.status})`
      );
    }
    const tenantModel = unwrapData<TenantModel>(tenantRes);
    if (!tenantModel) return null;
    return extractTenant(tenantModel);
  },

  /** Update only the slug for a tenant */
  changeTenantSlug: async (tenantId: UUID, slug: string) => {
    const res = await apiv1.changeTenantSlug(tenantId, { slug });
    return extractAccepted(res);
  },

  /**
   * Check whether a slug is available.
   * The generated adapter returns an Accepted model; use `ok` to determine
   * availability/success.
   */
  checkSlugAvailability: async (slug: string): Promise<boolean> => {
    const noCache = Date.now();
    const res = await apiv1.checkSlugReservation(slug, {
      no_cache: noCache,
    });

    if (!res) return false;

    // Available when server returns 2xx (204 No Content expected for available)
    if (res.ok) {
      if (res.status === 204) return true;
      // Some adapters may return a boolean in the body
      if (typeof res.data === 'boolean') return Boolean(res.data);
      return true;
    }

    // Not available when server returns 409 Conflict
    if (res.status === 409) return false;

    // Fallback: treat other non-OK responses as unavailable
    return false;
  },

  /** Update only the display name for a tenant */
  renameTenant: async (tenantId: UUID, name: string) => {
    const res = await apiv1.renameTenant(tenantId, { name });
    return extractAccepted(res);
  },

  /**
   * List tenants available for the user to join.
   * @param opts optional paging arguments
   * @returns array of Tenant objects (empty array if none)
   */
  listJoinableTenants: async (query?: PageRequest): Promise<Page<Tenant>> => {
    const apiQuery = { ...toApiQuery(query), joinable: true };
    const res = await apiv1.listMyTenants(apiQuery);
    const page = unwrapData<PageTenantModel>(res);
    const models: TenantModel[] = Array.isArray(page?.models)
      ? (page.models as TenantModel[])
      : [];
    const next = page?.next ?? null;
    const prev = page?.prev ?? null;
    return {
      items: models.map(extractTenant),
      next,
      prev,
      limit: query?.limit,
    };
  },
};
